import { PhoneIncoming, PhoneOutgoing, ExternalLink } from 'lucide-react';
import { Sheet } from '@/components/ui/Sheet';
import { Badge } from '@/components/ui/Badge';
import { InlineSelect } from '@/components/ui/InlineSelect';
import { SentimentIndicator } from '@/components/modules/SentimentIndicator';
import { useVoiceStore } from '@/stores/voiceStore';
import { formatDuration, formatRelative } from '@/lib/format';
import type { VoiceCall } from '@nexus/shared/types';

const statusOptions = [
  { value: 'queued', label: 'Queued' },
  { value: 'ringing', label: 'Ringing' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'failed', label: 'Failed' },
  { value: 'no_answer', label: 'No Answer' },
];

const statusVariant: Record<string, 'success' | 'warning' | 'error' | 'default'> = {
  completed: 'success',
  in_progress: 'success',
  no_answer: 'warning',
  failed: 'error',
  queued: 'default',
  ringing: 'default',
};

interface CallSheetProps {
  id: string | null;
  open: boolean;
  onClose: () => void;
}

export function CallSheet({ id, open, onClose }: CallSheetProps) {
  const { calls, update } = useVoiceStore();

  const call: VoiceCall | undefined = calls.find((c) => c.id === id);

  if (!call) {
    return (
      <Sheet open={open} onClose={onClose} title="Call">
        <div className="flex items-center justify-center py-12 text-surface-500">Loading...</div>
      </Sheet>
    );
  }

  const DirectionIcon = call.direction === 'inbound' ? PhoneIncoming : PhoneOutgoing;
  const dirColor = call.direction === 'inbound' ? 'text-blue-400' : 'text-emerald-400';

  return (
    <Sheet open={open} onClose={onClose} title="Call Details" width="lg">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start gap-4">
          <div className={`h-10 w-10 rounded-full bg-surface-800 border border-surface-700 flex items-center justify-center shrink-0 ${dirColor}`}>
            <DirectionIcon className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm text-surface-500 capitalize">{call.direction}</span>
              <InlineSelect
                value={call.status}
                options={statusOptions}
                onSave={async (v) => { await update(call.id, { status: v }); }}
              />
            </div>
            <div className="mt-2 flex items-center gap-4 text-sm">
              <div>
                <span className="text-xs text-surface-500">From: </span>
                <span className="text-surface-200 font-medium">{call.phoneFrom}</span>
              </div>
              <div>
                <span className="text-xs text-surface-500">To: </span>
                <span className="text-surface-200 font-medium">{call.phoneTo}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Details */}
        <div className="space-y-3">
          <h3 className="text-xs font-semibold text-surface-500 uppercase tracking-wider">Details</h3>
          <div className="grid grid-cols-[120px_1fr] gap-y-3 items-center">
            <span className="text-xs text-surface-500">Duration</span>
            <span className="text-sm text-surface-300">
              {call.duration && call.duration > 0 ? formatDuration(call.duration) : '--'}
            </span>

            <span className="text-xs text-surface-500">Sentiment</span>
            <SentimentIndicator sentiment={call.sentiment || 'neutral'} showLabel />

            <span className="text-xs text-surface-500">Escalated</span>
            {call.escalated ? (
              <Badge variant="error">Yes</Badge>
            ) : (
              <span className="text-sm text-surface-500">No</span>
            )}

            <span className="text-xs text-surface-500">Date</span>
            <span className="text-sm text-surface-300">{formatRelative(call.createdAt)}</span>

            {call.persona && (
              <>
                <span className="text-xs text-surface-500">Persona</span>
                <span className="text-sm text-surface-300">{call.persona}</span>
              </>
            )}
          </div>
        </div>

        {/* Transcription */}
        {call.transcription && (
          <div className="space-y-2">
            <h3 className="text-xs font-semibold text-surface-500 uppercase tracking-wider">Transcription</h3>
            <div className="bg-surface-800 border border-surface-700 rounded-lg p-4 max-h-64 overflow-y-auto">
              <p className="text-sm text-surface-300 whitespace-pre-wrap leading-relaxed">{call.transcription}</p>
            </div>
          </div>
        )}

        {/* Recording */}
        {call.recordingUrl && (
          <div className="space-y-2">
            <h3 className="text-xs font-semibold text-surface-500 uppercase tracking-wider">Recording</h3>
            <a
              href={call.recordingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-sm text-nexus-400 hover:text-nexus-300 transition-colors"
            >
              <ExternalLink className="h-3.5 w-3.5" />
              Open recording
            </a>
          </div>
        )}
      </div>
    </Sheet>
  );
}
